// Banner shown on borrower dashboard listing docs the broker requested,
// with per-item upload status + overall "X of Y" progress.
import React, { useEffect, useMemo, useState } from "react";
import { api, errorMessage } from "../lib/api";
import { Card } from "./ui/card";
import { Check, Circle } from "lucide-react";
import { toast } from "sonner";

const DOC_TYPE_LABEL = {
  executed_contract: "Executed Contract",
  drivers_license: "Driver's License / Government ID",
  credit_report: "Credit Report",
  bank_statement: "Bank Statements",
  purchase_agreement: "Purchase Agreement",
  rehab_budget: "Rehab Budget",
  government_id: "Government ID",
  bank_statements: "Bank Statements",
  proof_of_insurance: "Proof of Insurance",
  entity_documents: "Entity Documents",
  title_report: "Title Report",
  appraisal: "Appraisal",
  other: "Other",
};

export default function RequestedDocsBanner({ applicationId, requestedDocs }) {
  const [uploadedTypes, setUploadedTypes] = useState(new Set());

  useEffect(() => {
    if (!requestedDocs?.length) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/documents/application/${applicationId}`);
        if (cancelled) return;
        setUploadedTypes(new Set(data.map((d) => d.doc_type)));
      } catch (e) {
        if (!cancelled) toast.error(errorMessage(e));
      }
    })();
    return () => { cancelled = true; };
  }, [applicationId, requestedDocs]);

  const items = useMemo(() => {
    // "Other" can have multiple custom asks; uploaded check is type-based for non-other.
    return (requestedDocs || []).map((r, idx) => {
      const isOther = r.doc_type === "other";
      const fulfilled = isOther ? false : uploadedTypes.has(r.doc_type);
      return { key: `${r.doc_type}-${idx}`, ...r, fulfilled, isOther };
    });
  }, [requestedDocs, uploadedTypes]);

  if (!items.length) return null;

  const done = items.filter((i) => i.fulfilled).length;
  const total = items.length;
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);
  const allDone = done === total;

  return (
    <Card
      className="border-amber-500/20 bg-amber-500/[0.04] p-5"
      data-testid={`req-docs-banner-${applicationId}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-amber-200">
            {allDone ? "All requested documents uploaded" : "Documents requested by your broker"}
          </div>
          <div className="mt-1 text-xs text-slate-400">
            {allDone
              ? "Nice — your broker has everything they asked for."
              : "Please upload the items below. Use the Documents section on this card."}
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-semibold tabular-nums text-amber-200" data-testid="req-progress-count">
            {done} / {total}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">uploaded</div>
        </div>
      </div>

      <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-slate-800">
        <div
          className={`h-full rounded-full transition-all ${allDone ? "bg-emerald-500" : "bg-amber-400"}`}
          style={{ width: `${pct}%` }}
          data-testid="req-progress-bar"
        />
      </div>

      <ul className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {items.map((i) => (
          <li
            key={i.key}
            className={`flex items-start gap-3 rounded-md border p-3 text-sm transition-colors ${
              i.fulfilled
                ? "border-emerald-500/30 bg-emerald-500/[0.06] text-slate-200"
                : "border-slate-800 bg-slate-950 text-slate-300"
            }`}
            data-testid={`req-item-${i.key}`}
          >
            {i.fulfilled ? (
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
            ) : (
              <Circle className="mt-0.5 h-4 w-4 shrink-0 text-slate-600" />
            )}
            <div className="min-w-0">
              <div className="font-medium">
                {DOC_TYPE_LABEL[i.doc_type] || i.doc_type}
              </div>
              {i.label && (
                <div className="mt-0.5 truncate text-xs text-slate-400">{i.label}</div>
              )}
              {i.isOther && !i.fulfilled && (
                <div className="mt-0.5 text-[10px] uppercase tracking-wider text-slate-500">
                  Upload as &quot;Other&quot; below
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}
